import {
  isPrivateClientStrategyAlias,
  isStrategyAvailableForAdvisors,
} from "../../utils/proposalUtils";
import {
  createEmptyRegistrationDetails,
  getRegistrationDetailValidationIssue,
  normalizeRegistrationDetails,
} from "./ispBusinessRules";
import { createStandardRegistrationFields } from "./standardRegistrationUtils";

let nextFeeUpdateRowId = 1;

const createFeeUpdateRowId = () => `fee-update-account-${nextFeeUpdateRowId++}`;

export const createEmptyFeeUpdateAccount = () => ({
  _rowId: createFeeUpdateRowId(),
  accountLast4: "",
  accountOwner: "",
  registration: "",
  strategy: "",
  ...createStandardRegistrationFields(),
  ...createEmptyRegistrationDetails(),
});

const cleanText = (value) => String(value ?? "").trim();

const trimTrailingEmpty = (values) => {
  const trimmed = [...values];
  while (trimmed.length > 0 && !trimmed[trimmed.length - 1]) trimmed.pop();
  return trimmed;
};

const getLegacyAccountNumbers = (data = {}) => {
  if (Array.isArray(data.accountNumbers)) {
    return trimTrailingEmpty(data.accountNumbers.map(cleanText));
  }

  return cleanText(data.accountNumber)
    .split(/\s*,\s*/)
    .map(cleanText)
    .filter(Boolean);
};

const getLegacyRegistrations = (data = {}) => {
  if (Array.isArray(data.registrations)) {
    return trimTrailingEmpty(
      data.registrations.map((registration) =>
        cleanText(
          typeof registration === "string"
            ? registration
            : registration?.value || registration?.registrationName,
        ),
      ),
    );
  }

  return cleanText(data.registration)
    .split(/\s*,\s*/)
    .map(cleanText)
    .filter(Boolean);
};

export const normalizeFeeUpdateAccounts = (
  data = {},
  { ensureRow = true, registrationTypes = [] } = {},
) => {
  const hasStructuredAccounts = Array.isArray(data.feeUpdateAccounts);
  const structured = hasStructuredAccounts
    ? data.feeUpdateAccounts.map((account) => {
        const registration = cleanText(account?.registration);
        return {
          accountLast4: cleanText(
            account?.accountLast4 ??
              account?.accountNumber ??
              account?.last4,
          ),
          accountOwner: cleanText(account?.accountOwner ?? account?.name),
          registration,
          strategy: cleanText(account?.strategy),
          _rowId: cleanText(account?._rowId) || createFeeUpdateRowId(),
          ...createStandardRegistrationFields(account),
          ...normalizeRegistrationDetails(
            account,
            registration,
            registrationTypes,
          ),
        };
      })
    : [];

  if (hasStructuredAccounts) {
    return structured.length > 0 || !ensureRow
      ? structured
      : [createEmptyFeeUpdateAccount()];
  }

  const accountNumbers = getLegacyAccountNumbers(data);
  const registrations = getLegacyRegistrations(data);
  const legacyStrategies = Array.isArray(data.strategies)
    ? trimTrailingEmpty(data.strategies.map(cleanText))
    : [];
  const legacyStrategy = cleanText(data.strategy);
  const rowCount = Math.max(
    accountNumbers.length,
    registrations.length,
    legacyStrategies.length,
  );

  const migrated = Array.from({ length: rowCount }, (_, index) => {
    const registration =
      registrations.length === 1
        ? registrations[0]
        : registrations[index] || "";
    const legacyDetailsSource =
      rowCount === 1 ? data : createEmptyRegistrationDetails();
    return {
      _rowId: createFeeUpdateRowId(),
      accountLast4: accountNumbers[index] || "",
      accountOwner: "",
      registration,
      strategy: legacyStrategies[index] || legacyStrategy,
      ...createStandardRegistrationFields(
        rowCount === 1 ? data : {},
      ),
      ...normalizeRegistrationDetails(
        legacyDetailsSource,
        registration,
        registrationTypes,
      ),
    };
  });

  return migrated.length > 0 || !ensureRow
    ? migrated
    : [createEmptyFeeUpdateAccount()];
};

export const getFeeUpdateRegistrationDetailIssue = (
  accounts,
  { registrationTypes = [], required = true } = {},
) => {
  for (let index = 0; index < (accounts || []).length; index += 1) {
    const account = accounts[index] || {};
    const issue = getRegistrationDetailValidationIssue(
      account,
      account.registration,
      registrationTypes,
      { required },
    );
    if (issue) return `Account ${index + 1}: ${issue}`;
  }
  return "";
};

export const isFeeUpdateAccountComplete = (
  account = {},
  {
    registrationTypes = [],
    requireRegistrationDetails = false,
    requireAccountOwner = false,
    skipRegistrationDetails = false,
  } = {},
) => {
  const requiresDetails =
    !skipRegistrationDetails &&
    (requireRegistrationDetails || !!cleanText(account.registration));
  return (
    /^\d{4}$/.test(cleanText(account.accountLast4)) &&
    (!requireAccountOwner || !!cleanText(account.accountOwner)) &&
    !!cleanText(account.registration) &&
    !!cleanText(account.strategy) &&
    (!requiresDetails ||
      !getRegistrationDetailValidationIssue(
        account,
        account.registration,
        registrationTypes,
        { required: true },
      ))
  );
};

export const areFeeUpdateAccountsComplete = (accounts, options = {}) =>
  Array.isArray(accounts) &&
  accounts.length > 0 &&
  accounts.every((account) => isFeeUpdateAccountComplete(account, options));

export const getFeeUpdateStrategyCompatibilityIssue = (accounts) => {
  const strategies = Array.from(
    new Set(
      (accounts || [])
        .map((account) => cleanText(account?.strategy))
        .filter(Boolean),
    ),
  );
  if (strategies.length <= 1) return "";

  const hasSpecialFeeStructure = strategies.some((strategy) => {
    const normalized = strategy.toLowerCase();
    return (
      normalized.includes("fusion") ||
      isPrivateClientStrategyAlias(strategy) ||
      normalized.includes("elements") ||
      normalized.includes("managed money market solution")
    );
  });

  return hasSpecialFeeStructure
    ? "Fee Updates that combine different strategies cannot include Fusion, Private Client Strategies, Elements, or Managed Money Market Solutions, as those strategies require a dedicated ISP."
    : "";
};

export const getFeeUpdateAccountReferenceIssue = (
  accounts,
  { registrationTypes = [], proposalTypes = [], advisorNames = [] } = {},
) => {
  const registrations = new Set(
    registrationTypes.map((registration) => cleanText(registration?.name)).filter(Boolean),
  );

  for (let index = 0; index < (accounts || []).length; index += 1) {
    const account = accounts[index] || {};
    const registration = cleanText(account.registration);
    if (registration && !registrations.has(registration)) {
      return `Account ${index + 1} uses the unavailable Registration "${registration}". Select a current Registration before submitting.`;
    }

    const strategy = cleanText(account.strategy);
    if (!strategy) continue;
    const proposalType = proposalTypes.find(
      (candidate) => cleanText(candidate?.name) === strategy,
    );
    if (
      !proposalType ||
      proposalType.enabled === false ||
      !isStrategyAvailableForAdvisors(proposalType, advisorNames)
    ) {
      return `Account ${index + 1} uses the unavailable Strategy "${strategy}". Select a strategy available to the listed IARs before submitting.`;
    }
  }

  return "";
};

export const buildFeeUpdateLegacyFields = (accounts) => {
  const normalized = normalizeFeeUpdateAccounts(
    { feeUpdateAccounts: accounts },
    { ensureRow: true },
  );

  return {
    accountNumbers: normalized.map((account) => account.accountLast4),
    registrations: normalized.map((account, index) => ({
      registration: `registration${index}`,
      value: account.registration,
      percentage: "100",
    })),
    strategy: normalized[0]?.strategy || "",
  };
};
